import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataFetcher } from "components/DataFetcher";
import { ModalButtons } from "components/Modal/ModalButtons";
import { useModal } from "components/Modal/context/ModalContext";
import Pod from "components/Pod/Pod";
import useUserData from "hooks/useUserData";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFetchOptions } from "utils/fetch";
import "./CourseEditor.scss";
import ChangeList from "./components/ChangeList/ChangeList";
import ClickBoundary from "./components/ClickBoundary/ClickBoundary";
import CourseLock from "./components/CourseLock/CourseLock";
import EditButtonGroup from "./components/EditButton/EditButtonGroup";
import ModuleEditor from "./components/ModuleEditor/ModuleEditor";
import PublishStatus from "./components/PublishStatus/PublishStatus";
import { ChangeTrackingProvider, useChangeTracking } from "./hooks/useChangeTracking";
import { EditModeProvider, useEditMode } from "./hooks/useEditMode";
import { COURSE_DESCRIPTION_ROWS, EDIT_FIELD, EDIT_SCOPE, PUBLISH_STATUS } from "./utils/constants";
import { reorderContentItem, resetContentItemOrder, reorderContentItemsOnDelete, insertAndReorderContentItem } from "./utils/sortOrderHelpers";
import { 
	handleImageFileChange,
	handleMultilineInputKeyDown,
	handleSingleLineInputKeyDown,
	hasBeenEdited,
	openFilePicker
} from "./utils/uiInteractionHelpers";
import { UploadInProgressProvider } from "./Uploading/hooks/useUploadInProgress";

// DataFetcher request contexts.
const RequestContext = Object.freeze({
	GetCourse: "GetCourse",
	PutCoursePublish: "PutCoursePublish",
	PutCourseDraft: "PutCourseDraft",
	DeleteCourseDraft: "DeleteCourseDraft",
	PutCourseLock: "PutCourseLock",
	PostCourseUnlock: "PostCourseUnlock"
});

const CourseEditorInner = () => {
	const { setEditField, isEditing, cancelEditMode } = useEditMode();
	const location = useLocation();
	const [fetchData, setFetchData] = useState({});
	const [data, setData] = useState(null);
	const [locked, setLocked] = useState({hasTimestamp: false, byCurrentUser: false });
	const canEdit = useRef(false);
	const [nameDraft, setNameDraft] = useState("");
	const [descDraft, setDescDraft] = useState("");
	const [imageUrlDraft, setImageUrlDraft] = useState("");
	const imageFileDraftRef = useRef(null);
	const submoduleImagesFileDraftRef = useRef(null);
	const [modulesDraft, setModulesDraft] = useState(null);
	const { changes, rebuildChanges, resetChanges } = useChangeTracking();
	const [publishedStatus, setPublishedStatus] = useState(PUBLISH_STATUS.PUBLISHED);
	const courseId = useRef("");
	const courseDraft = useRef({});
	const { openModal } = useModal();
	const userData = useUserData();
	const navigate = useNavigate();
	const tempModuleIdRef = useRef(-1);
	const moduleRefs = useRef({});
	const [moduleToScroll, setModuleToScroll] = useState(null);

	// hidden file input for image uploads
	const fileInputRef = useRef(null);

	// This function does not need to be added to dependency arrays, as it is guaranteed stable (a useCallback with no dependencies).
	const setFetchRequestData = useCallback(({ url, options = null, context = null }) => {
		setFetchData({ url, options, context });
	}, []);

	// Effect that processes the URL hash.
	useEffect(() => {
		if (!location.hash) {
			return;
		}
		if (!userData.username) {
			return;
		}
		courseId.current = parseInt(location.hash.slice(1).split("-")[1], 10);
		setFetchRequestData({ url: `/api/v3/Course/${courseId.current}`, context: RequestContext.GetCourse });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.hash, userData.username]); // setFetchRequestData

	const initialiseCourseDraftData = (parsedDraft, fetchedData) => {
		const draftName = parsedDraft.Name ?? fetchedData.Name ?? "";
		const draftDesc = parsedDraft.Description ?? fetchedData.Description ?? "";
		const draftImage = parsedDraft.ImageUrl || fetchedData.ImageUrl || "";
		const draftModules = parsedDraft.Modules ? structuredClone(parsedDraft.Modules) : structuredClone(fetchedData.Modules ?? []);

		const allTempIds = (draftModules || [])
			.filter((m) => typeof m.Id === "number" && m.Id < 0)
			.map((m) => m.Id);
		tempModuleIdRef.current = allTempIds.length > 0 ? Math.min(...allTempIds) - 1 : -1;

		setNameDraft(draftName);
		setDescDraft(draftDesc);
		setImageUrlDraft(draftImage);
		imageFileDraftRef.current = submoduleImagesFileDraftRef.current = null;

		// This indirectly causes the useEffect in EditableModule.jsx to initialise data.
		setModulesDraft(draftModules);
	};

	const processData = useCallback((fetchedData) => {
		if (fetchedData.Id) {
			let parsedDraft = {};
			if (fetchedData.DraftData) {
				try {
					parsedDraft = JSON.parse(fetchedData.DraftData) || {};
				}
				catch {
					console.warn("Invalid DraftData JSON:", fetchedData.DraftData);
					parsedDraft = {};
				}
			}
			courseDraft.current = parsedDraft;
			const locked = {
				hasTimestamp: !!fetchedData.LockTimestamp,
				byCurrentUser: fetchedData.LockUsername === userData.username
			};
			setLocked(locked);
			canEdit.current = locked.hasTimestamp && locked.byCurrentUser;

			// Set draft data.
			initialiseCourseDraftData(parsedDraft, fetchedData);

			// Change list.
			const changeListPopulated = rebuildChanges(parsedDraft, fetchedData);
			setPublishedStatus(changeListPopulated ? PUBLISH_STATUS.UNPUBLISHED : PUBLISH_STATUS.PUBLISHED);
			const discardExistingDraft = (Object.entries(parsedDraft).length > 0 && !changeListPopulated);
			if (discardExistingDraft) {
				const options = getFetchOptions({
					method: "DELETE"
				});
				setFetchRequestData({ url: `/api/CourseDraft/${courseId.current}`, options, context: RequestContext.DeleteCourseDraft });
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userData.username, rebuildChanges]); // setFetchRequestData

	/**
	 * @note Fetch request different success return data:
	 * GET /api/v3/Course/{courseId} - CourseDataDto
	 * PUT /api/CourseDraft - CourseDataDto
	 * DELETE /api/CourseDraft/{courseId} - TRUE
	 * PUT /api/v3/Course - int
	 * PUT /api/CourseLock/{courseId} - CourseLockUserDto
	 * POST /api/CourseLock/{courseId}/Unlock - int
	 */
	const handleData = useCallback((fetchedData, token, context) => {
		if (!fetchedData) {
			return;
		}
		switch (context) {
			case RequestContext.GetCourse:
			case RequestContext.PutCourseDraft:
				if (isLockExpired(fetchedData.LockTimestamp, fetchedData.LockTimeoutMinutes)) {
					unlockCourse();
					return; // Don't process stale data, wait for unlock and refetch
				}
				if (!fetchedData.Editing) {
					setData(fetchedData);
					setPublishedStatus(fetchedData.CourseStatus);
				}
				processData(fetchedData);
				break;
			case RequestContext.PutCoursePublish:
			case RequestContext.PostCourseUnlock:
				setFetchRequestData({ url: `/api/v3/Course/${courseId.current}`, context: RequestContext.GetCourse });
				break;
			case RequestContext.PutCourseLock: {
				const { Username, Timestamp, CourseStatus } = fetchedData;
				const locked = {
					hasTimestamp: !!Timestamp,
					byCurrentUser: Username === userData.username
				};
				setLocked(locked);
				setData((prev) => {
					return {
						...prev,
						LockUsername: Username,
						LockTimestamp: Timestamp
					}
				});
				setPublishedStatus(CourseStatus);
				canEdit.current = locked.hasTimestamp && locked.byCurrentUser;
				break;
			}
			default:
				break;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processData, userData.username]); // setFetchRequestData

	const handleError = useCallback((error, context) => {
		const refreshMessage = "The page will refresh to load the latest version.";
		const errorMap = {
			"400-INVALID": {
				title: "Data Error",
				content: "The data sent to the server has not been received correctly - please contact Development for assistance. " + refreshMessage
			},
			"400-IMAGE": {
				title: "Image Removal Error",
				content: "The Course image could not be deleted and remains in Azure Storage. Please contact Development to fix this issue. " + refreshMessage
			},
			"404-PUBLISH": {
				title: "Publication Conflict",
				content: "This course has been modified by another user and cannot be published. " + refreshMessage
			},
			"404-DATA": {
				title: "Data Conflict",
				content: "The data has been modified by another user. " + refreshMessage
			}
		};
		let data = null;
		switch (error.Status) {
			case 400:
				if (error.Message === "ERR_DATA_INVALID") {
					data = errorMap["400-INVALID"];
				}
				else {
					switch (context) {
						case RequestContext.PutCoursePublish:
							data = {
								title: "Publication Error",
								content: <div>This course cannot be published:<br />{error.Message}<br />{refreshMessage}</div>
							};
							break;
						case RequestContext.DeleteCourseDraft:
							data = errorMap["400-IMAGE"];
							break;
						default:
							data = { title: "", content: error.Message };
							break;
					}
				}
				break;
			case 404:
				if (context === RequestContext.PutCoursePublish) {
					data = errorMap["404-PUBLISH"];
				}
				else {
					data = errorMap["404-DATA"];
				}
				break;
			case 409:
				data = { title: "", content: error.Message };
				break;
			default:
				break;
		}
		if (data) {
			openModal(data.title, data.content, ModalButtons.Ok, () => { navigate(0); });
		}
	},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[] // Adding "openModal" causes an infinite loop
	);

	const handleLockToggle = async () => {
		const options = getFetchOptions({
			method: "PUT"
		});
		setFetchRequestData({ url: `/api/CourseLock/${courseId.current}`, options, context: RequestContext.PutCourseLock });
	};

	const unlockCourse = async () => {
		const options = getFetchOptions({ method: "POST" });
		setFetchRequestData({ url: `/api/CourseLock/${courseId.current}/Unlock`, options, context: RequestContext.PostCourseUnlock });
	};

	const isLockExpired = (lockTimestamp, timeoutMinutes) => {
		if (!lockTimestamp) {
			return false;
		}
		const lockTime = new Date(lockTimestamp).getTime();
		const now = Date.now();
		return now - lockTime > timeoutMinutes * 60 * 1000;
	};

	// helpers for per-field undo (course-level)
	const fieldGetters = {
		[EDIT_FIELD.NAME]: () => nameDraft,
		[EDIT_FIELD.DESCRIPTION]: () => descDraft,
		[EDIT_FIELD.IMAGE_URL]: () => imageUrlDraft
	};

	const hasMissing = Array.isArray(changes) &&
		changes.some(block =>
			Array.isArray(block.changes) &&
			block.changes.some(ch => ch.label.toLowerCase().includes("missing"))
		);

	/**
	 * Required fields within CourseDraftDataDto:
	 * Id (int)
	 * Name (string)
	 * Description (string)
	 * ImageUrl (string)
	 * ImageFile (object? {FileName (string), MimeType (string), Base64 (string)})
	 * SubmoduleImageFiles (List<object?> {FileName (string), MimeType (string), Base64 (string)})
	 * Modules (List<ModuleDataDto>)
	 * PublishedCourse (CourseDataDto) - probably not needed now that we are doing commits in a callback
	 */
	const commitDraft = (overrides, cancelEditing = false) => {
		const body = {
			Id: courseId.current,
			Name: overrides?.[EDIT_FIELD.NAME] ?? nameDraft,
			Description: overrides?.[EDIT_FIELD.DESCRIPTION] ?? descDraft,
			ImageUrl: overrides?.[EDIT_FIELD.IMAGE_URL] ?? imageUrlDraft,
			ImageFile: overrides && Object.prototype.hasOwnProperty.call(overrides, "ImageFile") ? overrides.ImageFile : imageFileDraftRef.current,
			SubmoduleImageFiles: overrides?.SubmoduleImageFiles ?? submoduleImagesFileDraftRef.current ?? {},
			Modules: structuredClone(overrides?.Modules) ?? structuredClone(modulesDraft) ?? [],
			PublishedCourse: data
		};
		const options = getFetchOptions({ method: "PUT", body });
		setFetchRequestData({ url: "/api/CourseDraft", options, context: RequestContext.PutCourseDraft });
		if (cancelEditing) {
			cancelEditMode();
		}
	};

	//* This effect seems to do nothing...
	// Effect to handle scrolling.
	useEffect(() => {
		if (moduleToScroll == null) {
			return;
		}

		const el = moduleRefs.current[moduleToScroll];
		if (!el) {
			setModuleToScroll(null);
			return;
		}

		const rect = el.getBoundingClientRect();
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const offset = 20;

		window.scrollTo({
			top: rect.top + scrollTop - offset,
			behavior: "smooth"
		});

		setModuleToScroll(null);
	}, [modulesDraft, moduleToScroll]);
	//*/

	const handleCommitImageFile = (fileData) => {
		imageFileDraftRef.current = fileData;
		commitDraft({ ImageFile: fileData }, true);
	};

	const handleConfirmDiscard = (yes) => {
		if (yes) {
			const options = getFetchOptions({
				method: "DELETE"
			});
			setFetchRequestData({ url: `/api/CourseDraft/${courseId.current}`, options, context: RequestContext.DeleteCourseDraft });

			setNameDraft(data?.Name ?? "");
			setDescDraft(data?.Description ?? "");
			setImageUrlDraft(data?.ImageUrl ?? "");
			imageFileDraftRef.current = submoduleImagesFileDraftRef.current = null;
			setModulesDraft(data?.Modules ?? []);

			courseDraft.current = {};
			resetChanges();

			setData((prev) => (prev ? { ...prev, DraftData: "" } : prev));
		}
	};

	const handleConfirmPublish = (yes) => {
		if (yes) {
			const options = getFetchOptions({
				method: "PUT",
				body: {
					Id: courseId.current,
					Name: nameDraft,
					Description: descDraft,
					ImageUrl: imageUrlDraft,
					Modules: modulesDraft,
					DraftData: JSON.stringify(courseDraft.current)
				}
			});
			setFetchRequestData({ url: `/api/v3/Course/Publish`, options, context: RequestContext.PutCoursePublish });
		}
	};

	const handleOpenDiscardModal = () => {
		openModal(
			null,
			"Are you sure you want to discard changes to this course?",
			ModalButtons.YesNo,
			handleConfirmDiscard
		);
	};

	const handleOpenPublishModal = () => {
		openModal(
			null,
			"Are you sure you want to publish these changes? This will permanently overwrite the currently published course.",
			ModalButtons.YesNo,
			handleConfirmPublish
		);
	};

	const handleChangeModule = (updatedModule) => {
		const updatedDraftModules = modulesDraft.map((m) =>
			m.Id === updatedModule.Id ? updatedModule : m
		);
		setModulesDraft(updatedDraftModules);
		commitDraft({ Modules: updatedDraftModules }, true);
	};

	const handleAddModule = () => {
		const newModule = {
			Id: tempModuleIdRef.current,
			Name: "",
			Description: "",
			Active: true,
			Unlocked: true,
			ShowGuru: false,
			DeviceTraining: false,
			Position: modulesDraft.length,
			Submodules: []
		};
		const updatedModules = [...modulesDraft, newModule];
		setModulesDraft(updatedModules);
		commitDraft({ Modules: updatedModules });

		// Decrement Id reference for next created Module.
		tempModuleIdRef.current -= 1;
	};

	const handleDeleteModule = (moduleId) => {
		let updatedDraftModules = modulesDraft.filter(m => m.Id !== moduleId);
		updatedDraftModules = reorderContentItemsOnDelete(updatedDraftModules);
		setModulesDraft(updatedDraftModules);
		commitDraft({ Modules: updatedDraftModules }, true);
	};

	const handleChangeModulePosition = (moduleId, to) => {
		if (!data?.Modules?.length) {
			return;
		}

		const renumbered = reorderContentItem(modulesDraft, moduleId, to);
		setModulesDraft(renumbered);
		commitDraft({ Modules: renumbered });
		setModuleToScroll(moduleId);
	};

	const handleUpdateSubmoduleImageFile = (fileData) => {
		const { id, data } = fileData;

		const prev = submoduleImagesFileDraftRef.current;
		const setSubmoduleImagesFileDraft = (prev) => {
			// Remove entry if data is null.
			if (data == null) {
				if (prev == null) {
					return null;
				}

				const { [id]: _, ...rest } = prev;
				return Object.keys(rest).length > 0 ? rest : null;
			}

			// Initialise if null.
			if (prev == null) {
				return {
					[id]: data
				};
			}

			// Add or overwrite existing entry.
			return {
				...prev,
				[id]: data
			};
		};
		submoduleImagesFileDraftRef.current = setSubmoduleImagesFileDraft(prev);
	};

	const handleEditField = (field) => {
		setEditField({
			scope: EDIT_SCOPE.COURSE,
			field: field
		});
	};

	const handleUndoField = (field) => {
		const fieldSetters = {
			[EDIT_FIELD.NAME]: setNameDraft,
			[EDIT_FIELD.DESCRIPTION]: setDescDraft,
			[EDIT_FIELD.IMAGE_URL]: setImageUrlDraft
		};
		const publishedValue = data?.[field] ?? "";
		fieldSetters[field](publishedValue);
		commitDraft({
			[field]: publishedValue,
			...(field === EDIT_FIELD.IMAGE_URL ? { ImageFile: null } : {})
		});
		cancelEditMode();
	};

	const handleEditFieldFromChangeList = useCallback((scope, id, field) => {
		setEditField({ scope, id, field });

		if (scope === EDIT_SCOPE.COURSE) {
			if (field === EDIT_FIELD.NAME) {
				document.querySelector(".course-title")?.scrollIntoView({ behavior: "smooth", block: "center" });
			}

			if (field === EDIT_FIELD.DESCRIPTION) {
				document
					.querySelector(".course-container-details-text-description")
					?.scrollIntoView({ behavior: "smooth", block: "center" });
			}
			return;
		}

		if (scope === EDIT_SCOPE.MODULE) {
			const el = moduleRefs.current[id];
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}
	}, [setEditField]);

	const handleUndoFieldFromChangeList = (scope, id, parentId, field) => {
		if (scope === EDIT_SCOPE.COURSE) {
			if (field !== EDIT_FIELD.POSITION) {
				handleUndoField(field);
			}
			else {
				// Reset Module sort order.
				const updatedModules = resetContentItemOrder(modulesDraft, data?.Modules);
				setModulesDraft(updatedModules);
				commitDraft({ Modules: updatedModules });
				cancelEditMode();
			}
			return;
		}

		const moduleId = (scope === EDIT_SCOPE.MODULE) ? id : parentId;
		const publishedModule = data.Modules.find((m) => m.Id === moduleId);
		if (!publishedModule) {
			return;
		}

		// Module.
		if (scope === EDIT_SCOPE.MODULE) {
			if (field !== EDIT_FIELD.POSITION) {
				let module = modulesDraft.find((m) => m.Id === id);
				if (module) {
					const updatedModule = {
						...module,
						[field]: publishedModule[field]
					};
					handleChangeModule(updatedModule);
				}
			}
			else {
				// Reset Submodule sort order.
				const module = modulesDraft.find((m) => m.Id === id);
				if (module) {
					const updatedSubmodules = resetContentItemOrder(module.Submodules, publishedModule.Submodules);
					const updatedModule = {
						...module,
						Submodules: updatedSubmodules
					};
					handleChangeModule(updatedModule);
				}
			}
			return;
		}

		// Submodule.
		if (scope === EDIT_SCOPE.SUBMODULE) {
			const publishedSubmodule = publishedModule.Submodules.find((m) => m.Id === id);
			if (!publishedSubmodule) {
				return;
			}
			const module = modulesDraft.find((m) => m.Id === parentId);
			if (module) {
				const updatedModule = {
					...module,
					Submodules: module.Submodules.map(sm => sm.Id === id ? { ...sm, [field]: publishedSubmodule[field] } : sm)
				}
				handleChangeModule(updatedModule);
			}
		}
	};

	const handleUndoDeleteFromChangeList = (scope, id, parentId) => {
		const moduleId = (scope === EDIT_SCOPE.MODULE) ? id : parentId;
		const publishedModule = data.Modules.find((m) => m.Id === moduleId);
		if (!publishedModule) {
			return;
		}
		
		// Module.
		if (scope === EDIT_SCOPE.MODULE) {
			const updatedModules = insertAndReorderContentItem(modulesDraft, publishedModule);
			setModulesDraft(updatedModules);
			commitDraft({ Modules: updatedModules });
			cancelEditMode();
		}

		// Submodule.
		if (scope === EDIT_SCOPE.SUBMODULE) {
			const publishedSubmodule = publishedModule.Submodules.find((m) => m.Id === id);
			if (!publishedSubmodule) {
				return;
			}
			const module = modulesDraft.find((m) => m.Id === parentId);
			if (module) {
				const updatedSubmodules = insertAndReorderContentItem(module.Submodules, publishedSubmodule);
				const updatedModule = {
					...module,
					Submodules: updatedSubmodules
				};
				handleChangeModule(updatedModule);
			}
		}
	};

	const getPublishedModule = (m) => {
		const currentModule = data.Modules.find((dm) => dm.Id === m.Id);
		if (currentModule) {
			return currentModule;
		}
		const unpublishedDraftModule = courseDraft.current?.Modules?.find((dm) => dm.Id === m.Id);
		if (unpublishedDraftModule) {
			return unpublishedDraftModule;
		}
		return {
			Id: m.Id,
			Name: "",
			Description: "",
			Active: true,
			Unlocked: true,
			ShowGuru: false,
			DeviceTraining: false,
			Position: m.Position,
			Submodules: []
		};
	};
	
	return (
		<>
			<DataFetcher fetchData={fetchData} setData={handleData} setError={handleError} />
			{data !== null && (
				<div
					className="edit-course"
					onMouseDown={() => {
						setTimeout(cancelEditMode, 0);
					}}
				>
					<div className="grid-two-col gutters">
						<Pod title="Edit Course">
							<div className={`course-container${!canEdit.current ? " locked-readonly" : ""}`}>
								<div className="course-container-details">
									<div className="course-container-details-text">
										{/* Course Name */}
										<ClickBoundary>
											{isEditing(EDIT_SCOPE.COURSE, EDIT_FIELD.NAME) ? (
												<InputText
													id={"course-name-input"}
													autoFocus
													maxLength={100}
													value={nameDraft}
													onChange={(e) => setNameDraft(e.target.value)}
													onKeyDown={(e) => handleSingleLineInputKeyDown(e, cancelEditMode)}
													onBlur={() => commitDraft(null, true)}
													className="title-textbox"
												/>
											) : (
												<div className="course-title">
													<h1>{nameDraft || "Add Name"}</h1>
													<EditButtonGroup
														onEdit={() => handleEditField(EDIT_FIELD.NAME)}
														show={canEdit.current}
														showUndo={hasBeenEdited(data, fieldGetters, EDIT_FIELD.NAME)}
														onUndo={() => handleUndoField(EDIT_FIELD.NAME)}
													/>
												</div>
											)}
										</ClickBoundary>

										{/* Course Description */}
										<ClickBoundary>
											{isEditing(EDIT_SCOPE.COURSE, EDIT_FIELD.DESCRIPTION) ? (
												<InputTextarea
													id={"course-description-input"}
													maxLength={1000}
													value={descDraft}
													onChange={(e) => setDescDraft(e.target.value)}
													onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
													onBlur={() => commitDraft(null, true)}
													rows={COURSE_DESCRIPTION_ROWS}
													autoResize
													className="description-textbox"
												/>
											) : (
												<div className="course-container-details-text-description">
													<p>{descDraft || "Add Description"}</p>
													<EditButtonGroup
														mode="floating"
														onEdit={() => handleEditField(EDIT_FIELD.DESCRIPTION)}
														show={canEdit.current}
														showUndo={hasBeenEdited(data, fieldGetters, EDIT_FIELD.DESCRIPTION)}
														onUndo={() => handleUndoField(EDIT_FIELD.DESCRIPTION)}
													/>
												</div>
											)}
										</ClickBoundary>
									</div>
									
									{/* Course Image */}
									<div className="course-container-details-image">
										<img src={imageUrlDraft} alt={nameDraft} className="course-image" />
										<div className="course-container-details-image-edit">
											<input
												ref={fileInputRef}
												type="file"
												accept="image/*"
												style={{ display: "none" }}
												onChange={(e) => handleImageFileChange(e, fileInputRef, openModal, handleCommitImageFile)}
											/>
											<EditButtonGroup
												static
												editText="Edit Image"
												show={canEdit.current}
												onEdit={() => openFilePicker(fileInputRef)}
												showUndo={hasBeenEdited(data, fieldGetters, EDIT_FIELD.IMAGE_URL)}
												onUndo={() => handleUndoField(EDIT_FIELD.IMAGE_URL)}
											/>
										</div>
									</div>
								</div>

								<h2>Modules</h2>
								{data &&
									data.Modules &&
									Array.isArray(modulesDraft) &&
									modulesDraft
										.slice()
										.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
										.map((item, index, sortedModules) => (
											<ModuleEditor
												key={item.Id}
												canEdit={canEdit.current}
												module={getPublishedModule(item)}
												currentDraft={courseDraft.current?.Modules?.find((dm) => dm.Id === item.Id) ?? {}}
												index={index}
												total={sortedModules.length}
												onMove={(to) => handleChangeModulePosition(item.Id, to)}
												onMoveTop={() => handleChangeModulePosition(item.Id, 0)}
												onMoveBottom={() => handleChangeModulePosition(item.Id, data.Modules.length - 1)}
												onChange={handleChangeModule}
												onDelete={handleDeleteModule}
												onUpdateSubmoduleImageFile={handleUpdateSubmoduleImageFile}
											/>
										))}

								<div className="course-container-addnew">
									<div
										className="course-container-addnew-button"
										onClick={handleAddModule}
									>
										<p>
											Add New Module{" "}
											<FontAwesomeIcon
												icon={faPlusCircle}
												style={{ color: "#FFFFFF" }}
											/>
										</p>
									</div>
								</div>
							</div>
						</Pod>
						<Pod title="Changes" mobileOrder={1} sticky>
							<div>
								<CourseLock
									locked={locked}
									data={data}
									handleLockToggle={handleLockToggle}
								/>
								<div className={`${!canEdit.current ? "locked-readonly" : ""}`}>
									<PublishStatus
										status={publishedStatus}
										changes={changes}
										courseId={data.Id}
										handleOpenDiscardModal={handleOpenDiscardModal}
										handleOpenPublishModal={handleOpenPublishModal}
										hasMissing={hasMissing}
									/>
									{changes === null ? (
										<p>Loading changes...</p>
									) : (
										<ChangeList
											changes={changes}
											onEditField={handleEditFieldFromChangeList}
											onUndoField={handleUndoFieldFromChangeList}
											onUndoDelete={handleUndoDeleteFromChangeList}
											handleOpenDiscardModal={handleOpenDiscardModal}
										/>
									)}
								</div>
							</div>
						</Pod>
					</div>
				</div>
			)}
		</>
	);
};

const CourseEditor = () => (
	<EditModeProvider>
		<ChangeTrackingProvider>
			<UploadInProgressProvider>
				<CourseEditorInner />
			</UploadInProgressProvider>
		</ChangeTrackingProvider>
	</EditModeProvider>
);

export default CourseEditor;
